import {default as cx} from "classnames"
import {Calendar} from "primereact/calendar"
import {Dropdown} from "primereact/dropdown"
import {useState} from "react"
import useSWR, {useSWRConfig} from "swr"

import {wrappedPost} from "../../utilities/AxiosHelper"
import {
    fetcherGet,
    formatDateForApi,
    formatDateTime,
    getJSDate,
    getLocaleDate,
    ORIENTATION_STATUS,
} from "../../utilities/general"

export const EducatorProgressReport = ({week, orientation, osrs}) => {
    const [makePreceptor, setMakePreceptor] = useState(false)
    const {mutate: globalMutate} = useSWRConfig()
    const [notes, setNotes] = useState("")
    const [orientationEndDate, setOrientationEndDate] = useState(null)
    const [saving, setSaving] = useState(false)
    const [selectedRatingId, setSelectedRatingId] = useState(null)
    const [shiftedWeeksReasonId, setShiftedWeeksReasonId] = useState(null)
    const [preceptedShiftsInFinalWeek, setPreceptedShiftsInFinalWeek] = useState(null)
    const [weekDelta, setWeekDelta] = useState(0)

    const isPaused = orientation?.orientation_status_id === ORIENTATION_STATUS.PAUSED
    const rfpOsrs = (osrs ?? []).filter((osr) => osr?.osr_score?.percent >= 81)
    const chronologicalOsrs = [...(osrs ?? [])].sort((a, b) => {
        const weekDifference = (a?.week ?? 0) - (b?.week ?? 0)
        return weekDifference || String(a?.shift_date ?? "").localeCompare(String(b?.shift_date ?? ""))
    })
    let consecutiveRfpCount = 0
    let maxConsecutiveRfpCount = 0
    chronologicalOsrs.forEach((osr) => {
        consecutiveRfpCount = osr?.osr_score?.percent >= 81 ? consecutiveRfpCount + 1 : 0
        maxConsecutiveRfpCount = Math.max(maxConsecutiveRfpCount, consecutiveRfpCount)
    })
    const rfpWeekCount = new Set(rfpOsrs.map((osr) => osr.week).filter((week) => week != null)).size
    const rfpCount = rfpOsrs.length

    const {data: ratings} = useSWR(`/educators/progress-ratings/${orientation.id}`, fetcherGet, {
        fallbackData: [],
    })
    const {
        data: reports,
        isLoading: isReportsLoading,
        mutate: mutateReports,
    } = useSWR(`/educators/orientations/${orientation.id}/progress-reports`, fetcherGet, {
        fallbackData: [],
    })
    const {data: shiftedWeeksReasons} = useSWR(
        `/educators/orientations/${orientation.id}/shifted-weeks-reasons`,
        fetcherGet,
        {fallbackData: []}
    )
    const expectedEndDate = () => {
        if (!orientation?.start_date) return ""

        const endDate = getJSDate(orientation.expected_end_date)
        if (!endDate) return ""

        endDate.setDate(endDate.getDate() + weekDelta * 7)
        return getLocaleDate(endDate)
    }
    const saveEnabled = () => {
        if (!selectedRatingId && (weekDelta === 0 || (weekDelta !== 0 && !shiftedWeeksReasonId))) return false
        if (selectedRatingId === -1 && (!orientationEndDate || !preceptedShiftsInFinalWeek)) return false
        return true
    }
    const handleAddNote = () => {
        if (!notes || notes.trim() === "") return

        setSaving(true)
        const payload = {week_num: week, notes: notes}
        wrappedPost(`/educators/orientations/${orientation.id}/progress-report-note`, payload)
            .then(() => {
                setNotes("")
                mutateReports()
            })
            .finally(() => {
                setSaving(false)
            })
    }
    const handleSave = () => {
        if (!saveEnabled()) return
        setSaving(true)
        const payload = {week_num: week}
        if (selectedRatingId) {
            payload.rating_id = selectedRatingId
            if (selectedRatingId === -1) {
                payload.make_user_preceptor = makePreceptor
                payload.orientation_end_date = formatDateForApi(orientationEndDate)
                payload.precepted_shifts_in_final_week = preceptedShiftsInFinalWeek
            }
        }
        if (weekDelta !== 0) {
            payload.educator_shifted_weeks = weekDelta
            payload.shift_reason_id = weekDelta !== 0 ? shiftedWeeksReasonId : null
        }
        wrappedPost(`/educators/orientations/${orientation.id}/progress-reports`, payload)
            .then(() => {
                setNotes("")
                setSelectedRatingId(null)
                setWeekDelta(0)
                mutateReports()
                // Refresh the details page cache so the update shows immediately when returning.
                globalMutate(
                    `/orientation-details?orientation-id=${orientation.id}`,
                    fetcherGet(`/orientation-details?orientation-id=${orientation.id}`),
                    {revalidate: false}
                )
            })
            .finally(() => {
                setSaving(false)
            })
    }

    return (
        <>
            {rfpCount > 0 && (
                <div className="row px-4 mb-4">
                    {orientation.name} has scored ready for practice on {rfpCount} OSR{rfpCount === 1 ? "" : "s"},{" "}
                    {maxConsecutiveRfpCount} consecutively, across {rfpWeekCount} week{rfpWeekCount === 1 ? "" : "s"}.
                </div>
            )}
            <div className="d-flex flex-row h3 mb-4">Week {week} Progress Report </div>
            <div className="form-label h4 fs-4 required">Progress Rating</div>
            <div className="btn-group d-grid d-sm-flex gap-2 gap-sm-0 w-100 mb-10">
                {(ratings ?? []).map((rating) => (
                    <button
                        key={rating.id}
                        type="radio"
                        className={cx("btn btn-sm btn-outline btn-active-primary px-0", {
                            active: selectedRatingId === rating.id,
                        })}
                        disabled={isPaused}
                        onClick={() => setSelectedRatingId(rating.id)}
                    >
                        {rating.name}
                    </button>
                ))}
                {rfpCount > 0 && (
                    <button
                        type="radio"
                        className={cx("btn btn-sm btn-outline btn-active-primary px-0", {
                            active: selectedRatingId === -1,
                        })}
                        disabled={isPaused}
                        onClick={() => setSelectedRatingId(-1)}
                    >
                        Ready for Practice
                    </button>
                )}
            </div>
            {selectedRatingId === -1 && (
                <div className="row">
                    <div className="col-12 mb-4">
                        <label className="form-label fw-bold required">Orientation End Date</label>
                        <Calendar
                            value={orientationEndDate}
                            onChange={(e) => setOrientationEndDate(e.value)}
                            disabled={isPaused}
                            showIcon={true}
                            maxDate={new Date()}
                            minDate={getJSDate(orientation.start_date)}
                        />
                    </div>
                    <div className="col-12 mb-4">
                        <label className="form-label fw-bold required">Number of precepted shifts in final week</label>
                        <input
                            type="number"
                            min={0}
                            className="form-control form-control-solid"
                            value={preceptedShiftsInFinalWeek}
                            onChange={(e) =>
                                setPreceptedShiftsInFinalWeek(e.target.value === "" ? null : Number(e.target.value))
                            }
                        />
                    </div>
                    <div className="col-12 mb-8">
                        <label className="form-label required">Convert User to Preceptor</label>
                        <div className="d-flex flex-row flex-center flex-md-stack gap-2">
                            <div className="flex-fill">
                                <input
                                    id="id-make-preceptor-no"
                                    type="radio"
                                    className="btn-check"
                                    name="make_preceptor"
                                    value={false}
                                    checked={!makePreceptor}
                                    onChange={() => setMakePreceptor(false)}
                                />
                                <label
                                    className={cx("btn btn-outline btn-active-primary btn-flex flex-center w-100", {
                                        active: !makePreceptor,
                                    })}
                                    htmlFor="id-make-preceptor-no"
                                >
                                    No
                                </label>
                            </div>
                            <div className="flex-fill">
                                <input
                                    id="id-make-preceptor-yes"
                                    type="radio"
                                    className="btn-check"
                                    name="make_preceptor"
                                    value={true}
                                    checked={makePreceptor}
                                    onChange={() => setMakePreceptor(true)}
                                />
                                <label
                                    className={cx("btn btn-outline btn-active-primary btn-flex flex-center w-100", {
                                        active: makePreceptor,
                                    })}
                                    htmlFor="id-make-preceptor-yes"
                                >
                                    Yes
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <h4 className="mb-4">Adjust Expected Orientation Length</h4>
            <div className="row">
                <div className="col-lg-6 mb-4">
                    <label className="form-label fw-bold">Add or Subtract Weeks</label>
                    <div className="input-group">
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={isPaused}
                            onClick={() => setWeekDelta(weekDelta - 1)}
                        >
                            <i className="fas fa-minus"></i>
                        </button>
                        <input type="number" className="form-control text-center" readOnly value={weekDelta} />
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={isPaused}
                            onClick={() => setWeekDelta(weekDelta + 1)}
                        >
                            <i className="fas fa-plus"></i>
                        </button>
                    </div>
                </div>
                <div className="col-lg-6 mb-4">
                    <label className="form-label fw-bold">{weekDelta !== 0 ? "New " : ""}Expected End Date</label>
                    <input type="text" className="form-control" readOnly value={expectedEndDate()} placeholder="—" />
                </div>
            </div>
            {weekDelta !== 0 && (
                <div className="row">
                    <div className="col-12 mb-4">
                        <label className="form-label fw-bold required">Reason for Addition or Subtraction</label>
                        <Dropdown
                            options={shiftedWeeksReasons
                                .filter((reason) => {
                                    const adding = weekDelta > 0
                                    return reason.adding === adding || reason.adding === null
                                })
                                .map((reason) => ({value: reason.id, label: reason.name}))}
                            value={shiftedWeeksReasonId}
                            onChange={(event) => setShiftedWeeksReasonId(event.value)}
                            placeholder="Select a reason"
                            className="w-100"
                        />
                    </div>
                </div>
            )}
            <div className="col-12 my-4 d-flex justify-content-center">
                <button
                    className="btn btn-primary mb-4"
                    disabled={isPaused || saving || !saveEnabled()}
                    onClick={handleSave}
                >
                    {saving ? "Saving..." : "Save"}
                </button>
            </div>
            <hr className="mt-6 mb-4" />
            <div className="w-100 py-5">
                <label htmlFor="add-note" className="mb-2 h4 fs-4">
                    Notes
                </label>
                <div className="w-100 d-flex flex-row align-items-center gap-2">
                    <textarea
                        id="add-note"
                        rows={3}
                        className="form-control fs-4"
                        placeholder="Add a note"
                        value={notes}
                        disabled={isPaused}
                        onChange={(evt) => setNotes(evt.target.value)}
                    />
                    <button className="btn btn-primary mt-4" disabled={isPaused || saving} onClick={handleAddNote}>
                        {saving ? "Adding..." : "Add"}
                    </button>
                </div>
            </div>
            {isReportsLoading ? (
                <div>Loading...</div>
            ) : (
                reports &&
                reports.length > 0 && (
                    <>
                        <hr className="mb-6" />
                        {reports.map((report) => (
                            <div key={report.id} className="border-bottom py-4 text-break">
                                <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-1 gap-sm-0 mb-2">
                                    <div>
                                        <div className="fw-bold">{report.author_name}</div>
                                        <div className="">Week {report.week}</div>
                                    </div>
                                    <div className="fs-7 text-end">
                                        <div className="text-muted">{formatDateTime(report.created_at)}</div>
                                    </div>
                                </div>
                                {report.rating_name && <div className="text-muted fs-7 mb-2">{report.rating_name}</div>}
                                {report.educator_shifted_weeks != null && (
                                    <div className="text-primary fs-7 mt-1">
                                        <i className="fas fa-calendar-week me-2"></i>
                                        Expected orientation length adjusted:{" "}
                                        {report.educator_shifted_weeks > 0 ? "+" : ""}
                                        {report.educator_shifted_weeks} week
                                        {Math.abs(report.educator_shifted_weeks) === 1 ? "" : "s"}
                                        {report.orientation_shifted_weeks_reason_id &&
                                            ` — ${shiftedWeeksReasons.filter((r) => r.id === report.orientation_shifted_weeks_reason_id)[0]?.name}`}
                                    </div>
                                )}
                                {report.notes && (
                                    <div style={{whiteSpace: "pre-wrap"}} className="text-break">
                                        {report.notes}
                                    </div>
                                )}
                            </div>
                        ))}
                    </>
                )
            )}
        </>
    )
}
